import React from 'react'

import { useToast } from '@wartek-id/toast'

import { useAPIPhicosRedirect } from 'api/external/useAPIExternal'
import { useAPIGetToggles } from 'api/toggle/useAPIToggle'
import { useTracker } from 'api/tracker/useAPITracker'

import { DEFAULT_ERROR_MESSAGE, TOGGLE_KEYS, URL_HELPDESK } from 'app/resources'

import { useRegionContext } from 'utils/hooks/useRegion'
import { useRoles } from 'utils/hooks/useRoles'

import CustomVerticalNavigation from 'components/CustomVerticalNavigation'
import type { NavigationItem } from 'components/CustomVerticalNavigation/types'

export const DesktopSidebar = () => {
  const { satuEventTrack } = useTracker()
  const toast = useToast()

  const {
    isAdminUser,
    isDinasUser,
    isAdminUPTUser,
    isSuperAdminUser,
    isGtkUser,
    isKemendagriUser,
  } = useRoles()

  const { regionDetail } = useRegionContext()
  const {
    refetch: getRedirectUrl,
    isLoading: isGettingRedirectUrl,
    remove: removeRedirectUrl,
  } = useAPIPhicosRedirect({
    regionId: regionDetail.id,
    regionType: regionDetail.type,
  })

  const { data: resToggleRevampKSActive } = useAPIGetToggles(
    TOGGLE_KEYS.REVAMP_KS_ACTIVE_ENABLED
  )

  const { data: resTogglePenyesuaianVervalPeriodisasi } = useAPIGetToggles(
    TOGGLE_KEYS.PENYESUAIAN_VERVAL_PERIODISASI_ENABLED
  )

  const { data: resToggleDasborPengajuanPerubahan } = useAPIGetToggles(
    TOGGLE_KEYS.DASBOR_PENGAJUAN_PERUBAHAN_ENABLED
  )

  const { data: resToggleVervalSkPppkUpt } = useAPIGetToggles(
    TOGGLE_KEYS.VERVAL_SK_PPPK_UPT_ENABLED
  )

  const handleRedirectToPhicos = async () => {
    try {
      const { data } = await getRedirectUrl()
      window.open(data.redirectUrl)
      removeRedirectUrl()
    } catch {
      toast({
        message: DEFAULT_ERROR_MESSAGE,
      })
    }
  }

  const sidebarMenuListKemendagri: NavigationItem[] = []

  const sidebarMenuList: NavigationItem[] = [
    {
      icon: 'home',
      label: 'Beranda',
      href: '/home',
    },
    ...(isSuperAdminUser && resToggleDasborPengajuanPerubahan?.toggle.isEnabled
      ? [
          {
            icon: 'dashboard',
            label: 'Dasbor',
            href: '/dashboard/pengajuan-perubahan',
          },
        ]
      : []),
    {
      icon: 'supervisor_account',
      label: 'Kepala Sekolah (KS)',
      href: '',
      children: [
        {
          label: 'Pemetaan Kebutuhan KS',
          href: '/ks/demand',
        },
        {
          label: 'Guru Memenuhi Syarat',
          href: '/ks/supply',
        },
        {
          label: 'Pengusulan',
          href: '/ks/selection/proposals',
        },
        {
          label: 'Seleksi',
          href: '',
          children: [
            {
              label: 'Seleksi Administrasi',
              href: '/ks/selection/administration',
            },
            ...(isDinasUser || isAdminUPTUser
              ? [
                  {
                    label: 'Seleksi Substansi',
                    href: '',
                    onClick() {
                      handleRedirectToPhicos()
                    },
                    isLoading: isGettingRedirectUrl,
                  },
                ]
              : []),
          ],
        },
        {
          label: 'Pelatihan',
          href: '/ks/training',
        },
        {
          label: 'Penugasan',
          href: '',
          children: [
            {
              label: 'Reguler',
              href: '/ks/assignment/regular',
            },
            {
              label: 'Non-Reguler',
              href: '/ks/assignment/non-regular-and-mutation',
            },
            {
              label: 'Swasta Murni',
              href: '/ks/assignment/swasta-murni',
            },
          ],
        },
        {
          label: 'Pemberhentian',
          href: '/ks/termination',
        },
        ...[
          resToggleRevampKSActive?.toggle.isEnabled
            ? { label: 'Manajemen KS', href: '/ks/manajemen-ks' }
            : {
                label: 'Manajemen KS Aktif',
                href: '/ks/active',
              },
        ],
        {
          label: 'Riwayat Penugasan',
          href: '/ks/history-selection',
        },
      ],
    },
    {
      icon: 'supervisor_account',
      label: 'Pengawas Sekolah (PS)',
      href: '',
      children: [
        {
          label: 'Kebutuhan PS',
          href: '/ps/demand',
          onClick() {
            satuEventTrack({ event_name: 'view_demand_ps' })
          },
        },
        {
          label: 'Daftar Ketersediaan BCPS',
          href: '/ps/supply',
          onClick() {
            satuEventTrack({ event_name: 'view_supply_bcps' })
          },
        },
        ...(!isAdminUPTUser && (isAdminUser || isGtkUser)
          ? [
              {
                label: 'Daftar Pengajuan Formasi',
                href: '/ps/allocation-requests',
              },
            ]
          : []),
        ...(!isAdminUPTUser && (isAdminUser || isGtkUser)
          ? [
              {
                label: 'Daftar Persetujuan Formasi',
                href: '/ps/formation-approval-requests',
              },
            ]
          : []),
      ],
    },
    {
      icon: 'supervisor_account',
      label: 'Tenaga Kependidikan (TK)',
      href: '/tk',
      children: [],
    },
    ...(isDinasUser ||
    (isAdminUPTUser &&
      (resTogglePenyesuaianVervalPeriodisasi?.toggle.isEnabled ||
        isSuperAdminUser ||
        resToggleVervalSkPppkUpt?.toggle.isEnabled))
      ? [
          {
            icon: 'description',
            label: 'Pengajuan Perubahan',
            href: '/pengajuan-perubahan',
            children: [],
          },
        ]
      : []),
    {
      icon: 'help',
      label: 'Butuh Bantuan',
      href: URL_HELPDESK,
      newTab: true,
      onClick() {
        satuEventTrack({ event_name: 'click_bantuan' })
      },
    },
    ...(isAdminUser || isAdminUPTUser
      ? [
          {
            icon: 'supervised_user_circle',
            label: 'Manajemen Akses',
            href: '/access_management',
          },
        ]
      : []),
  ]

  return (
    <div
      className="fixed z-40 h-full w-[316px] bg-expressive-dark-blue p-4 shadow-elevation-01"
      data-testid="sidebar"
    >
      <nav className="max-h-[calc(100vh-8rem)] overflow-y-auto">
        <CustomVerticalNavigation
          items={isKemendagriUser ? sidebarMenuListKemendagri : sidebarMenuList}
        />
      </nav>
    </div>
  )
}
